import React, { useState } from "react";
import Countdown from "react-countdown";
import { FaCreditCard, FaPaperPlane } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/app/global-components/card";
import { isEmpty } from "lodash";
import "./styles.css";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import useOffer from "@/hooks/useTender";

interface Opportunity {
  city: string;
  region: string;
  categories: any;
  endDate: string | number | Date | undefined;
  id: number;
  title: string;
  category: string;
  location: string;
  status: string;
  remainingTime: number;
  companyId: string;
  companyDef?: any;
}

interface OpportunityCardProps {
  opportunity: Opportunity;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const statuses = [
    {
      label: t("status.planning"),
      name: "Planning",
      backgroundColor: "#e0f7fa",
      color: "#00796b",
    },
    {
      label: t("status.planning"),
      name: "Pending",
      backgroundColor: "#e0f7fa",
      color: "#00796b",
    },
    {
      label: t("status.opened"),
      name: "Opened",
      backgroundColor: "#e3f2fd",
      color: "#3f77a4",
    },
    {
      label: t("status.closed"),
      name: "Closed",
      backgroundColor: "#e0e0e0",
      color: "#616161",
    },
    {
      label: t("status.accepted"),
      name: "Accepted",
      backgroundColor: "#cdeeeb",
      color: "#00a046",
    },
    {
      label: t("status.payment"),
      name: "Payment",
      backgroundColor: "#ffecb3",
      color: "#ff6f00",
    },
    {
      label: t("status.paymentConfirmation"),
      name: "PaymentConfirmation",
      backgroundColor: "#ffe0b2",
      color: "#bf360c",
    },
    {
      label: t("status.sendingLabel"),
      name: "Sending",
      backgroundColor: "#d1c4e9",
      color: "#512da8",
    },
    {
      label: t("status.receiving"),
      name: "Receiving",
      backgroundColor: "#dcedc8",
      color: "#33691e",
    },
    {
      label: t("status.finished"),
      name: "Finished",
      backgroundColor: "#ffccbc",
      color: "#bf360c",
    },
    {
      label: t("status.canceled"),
      name: "Canceled",
      backgroundColor: "#ffcdd2",
      color: "#c62828",
    },
  ];
  const [status, setStatus] = useState<string>(opportunity.status);
  const { sendOffer } = useOffer();
  const navigate = useNavigate();

  const getStatusLabel = (status: string) => {
    const statusProps = statuses.find((s) => s.name === status) || {
      label: t("status.unknown"),
      backgroundColor: "#f0f0f0",
      color: "#9e9e9e",
    };
    return statusProps.label;
  };

  const handleSendOpportunity = (e: React.MouseEvent) => {
    e.stopPropagation();
    sendOffer(opportunity.id.toString(), "Opened");
  };

  const primaryColor = "#644980";
  const handleCountdownComplete = () => {
    setStatus("ended");
  };
  return (
    <Card
      key={opportunity.id}
      onClick={() => navigate(`${routes.vuetender}/${opportunity.id}`)}
      className="md:w-70 bg-white border-gray-100 w-50 h-full flex flex-col transform transition-transform duration-300 shadow-deffault cursor-pointer"
    >
      <CardHeader className="flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div
            className="text-sm custom-status font-medium"
            style={{ color: "#fff", background: primaryColor }}
          >
            {status === "ended"
              ? t("time.ended")
              : getStatusLabel(opportunity.status)}
          </div>
          <div
            className="text-secondary-brightYellow"
            style={{ color: primaryColor }}
          >
            <FaCreditCard size={24} />
          </div>
        </div>
        <div>{opportunity.companyDef?.CompanyNr}</div>
      </CardHeader>

      <CardContent className="flex-1">
        <CardTitle className="text-xl font-bold text-gray-900 mb-2">
          {opportunity.title}
        </CardTitle>
        <div className="text-sm text-gray-600 mb-2">
          {isEmpty(opportunity.categories)
            ? ""
            : opportunity.categories
                .slice(0, 5)
                .map((item: any) => `${item}, `)}
        </div>
        <div className="text-sm text-gray-600">
          {opportunity.region}, {opportunity.city}
        </div>
      </CardContent>
      <CardFooter className="p-4 border-t border-purple-200 bg-gray-50 rounded-b-lg flex flex-col items-center gap-2">
        <div className="text-sm text-gray-500 text-center px-4 rounded-xl py-2 border border-gray-200 bg-gray-50 font-bold">
          {["Planning", "Pending"].includes(opportunity.status) ? (
            <button
              className="text-white px-4 py-2 rounded-md flex items-center gap-2 transition-all"
              style={{ backgroundColor: primaryColor }}
              onClick={handleSendOpportunity}
            >
              <FaPaperPlane />
              {t("actions.sendOpportunity")}
            </button>
          ) : (
            <Countdown
              date={opportunity.endDate}
              onComplete={handleCountdownComplete} // Use onComplete instead of setting state in renderer
              renderer={({ days, hours, minutes, seconds, completed }) => {
                if (completed) {
                  return <span>{t("time.ended")}</span>;
                }
                if (days > 0) {
                  return (
                    <span>
                      {days + 1} {t("time.day")}
                    </span>
                  );
                }
                return (
                  <span>
                    {hours} {t("time.hour")} {minutes} {t("time.minute")}{" "}
                    {seconds} {t("time.second")}
                  </span>
                );
              }}
            />
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default OpportunityCard;
